import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';

const YELLOW = '#F4C430';

/**
 * Link an inventory item to another printer.
 *  - Picks a printer not already linked.
 *  - If the target printer has a same-name item, asks to merge (sum stocks).
 */
export const LinkPrinterModal = ({ open, onClose, onLinked, item }) => {
  const { data: printers } = useApi('/printers');
  const [pickedId, setPickedId] = useState(null);
  const [conflict, setConflict] = useState(null);  // { conflictItem }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setPickedId(null);
      setConflict(null);
      setError(null);
    }
  }, [open]);

  const linkedIds = useMemo(
    () => new Set((item?.linkedPrinters ?? []).map((p) => p.id)),
    [item],
  );

  const candidates = useMemo(
    () => (printers ?? []).filter((p) => !linkedIds.has(p.id) && p.status !== 'decommissioned'),
    [printers, linkedIds],
  );

  if (!open || !item) return null;

  const tryLink = async () => {
    if (!pickedId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`/inventory/${item.id}/link-printer`, { printerId: pickedId });
      if (result.requiresMerge) {
        setConflict(result.conflictItem);
        return;
      }
      onLinked?.(result.item);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const confirmMerge = async () => {
    if (!conflict) return;
    setBusy(true);
    setError(null);
    try {
      // merge the conflicting source INTO this item, then link the picked printer
      await api.post(`/inventory/${item.id}/merge`, { sourceId: conflict.id });
      // The merge automatically transfers the printer link too. Refetch.
      const refreshed = await api.get(`/inventory/${item.id}`);
      onLinked?.(refreshed);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: YELLOW }}>Link printer</p>
          <p class="m-0 font-serif text-lg font-medium text-text-primary">{item.name}</p>
          <p class="m-0 mt-0.5 text-[12px] text-text-tertiary">
            Currently linked to {item.linkedPrinters.length || 'no'} printer{item.linkedPrinters.length === 1 ? '' : 's'}
          </p>
        </div>

        <div class="space-y-4 p-5">
          {!conflict ? (
            <>
              {candidates.length === 0 ? (
                <p class="text-sm text-text-tertiary">
                  All eligible printers are already linked.
                </p>
              ) : (
                <div>
                  <p class="mb-2 text-[12px] font-semibold text-text-tertiary">Link to:</p>
                  <div class="space-y-1.5">
                    {candidates.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPickedId(p.id)}
                        class={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left transition ${
                          pickedId === p.id
                            ? 'border-ink-yellow bg-ink-yellow/[0.06]'
                            : 'border-border-strong bg-bg-card hover:bg-bg-hover'
                        }`}
                      >
                        <div>
                          <p class="m-0 text-sm font-semibold text-text-primary">{p.name}</p>
                          <p class="m-0 text-[11px] text-text-tertiary">
                            {p.model ?? p.kind} {p.status === 'semi_active' && '· ⚠ semi-active'}
                          </p>
                        </div>
                        {pickedId === p.id && <span class="font-bold text-ink-yellow">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {error && (
                <p class="text-[13px] font-semibold text-ink-magenta">
                  {error instanceof ApiError ? error.message : (error.message ?? 'Failed to link')}
                </p>
              )}
            </>
          ) : (
            <div class="rounded-md border border-ink-yellow/[0.3] bg-ink-yellow/[0.06] p-3">
              <p class="m-0 mb-1 text-[13px] font-semibold text-ink-yellow">
                That printer already has "{conflict.name}".
              </p>
              <p class="m-0 mb-3 text-[12px] font-medium text-text-secondary">
                It currently holds <span class="font-serif text-text-primary">{conflict.stockCount}</span> in stock.
                Merge it into this item? Stocks will sum to{' '}
                <span class="font-serif text-text-primary">{item.stockCount + conflict.stockCount}</span> and the
                other item's log history will move here. This can't be undone.
              </p>
              {error && (
                <p class="text-[13px] font-semibold text-ink-magenta">
                  {error instanceof ApiError ? error.message : (error.message ?? 'Failed to merge')}
                </p>
              )}
            </div>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          {!conflict ? (
            <Button variant="primary" onClick={tryLink} disabled={busy || !pickedId}>
              {busy ? 'Linking…' : 'Link'}
            </Button>
          ) : (
            <Button variant="primary" onClick={confirmMerge} disabled={busy}>
              {busy ? 'Merging…' : 'Merge & link'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
